<!--
  ReportViewScreen.vue  —  Engine E, the interactive report viewer shell.
  Purpose: register the 'report-view' Vuex module (idempotently, in beforeCreate),
    watch $route.params.id to fetch the report document, render report.title +
    each sheet as a v-tab, and mount each section through the section2cmp
    registry: <component :is="section2cmp[s.type] || 'report-section-not-found'">.

  reconstructed from lacak.io deployed behavior; app-specific contract verbatim,
  internal impl re-authored.

  VERBATIM (ad4f.js render fn):
    - component name .......... "report-view-screen"
    - registerModuleSafe("report-view", module, true) in beforeCreate
    - watch $route.params.id (immediate) -> dispatch report-view/fetch
    - report.title -> title; report.sheets[] -> tabs; sheet.sections[] -> sections
    - <component :is="section2cmp[s.type] || 'report-section-not-found'" :section="s">
-->
<template>
  <div class="report-view-screen">
    <!-- report builder: object + period + format → generate (poll @200ms) → download -->
    <v-card class="report-builder pa-3 mb-3">
      <div class="subheading mb-2">Generate report</div>
      <v-layout row wrap align-center>
        <v-flex xs12 sm4 class="px-1">
          <v-select :items="trackerItems" v-model="builder.trackerId" label="Object" dense hide-details></v-select>
        </v-flex>
        <v-flex xs6 sm2 class="px-1"><v-text-field type="date" v-model="builder.from" label="From" dense hide-details></v-text-field></v-flex>
        <v-flex xs6 sm2 class="px-1"><v-text-field type="date" v-model="builder.to" label="To" dense hide-details></v-text-field></v-flex>
        <v-flex xs6 sm2 class="px-1"><v-select :items="['pdf','xlsx']" v-model="builder.format" label="Format" dense hide-details></v-select></v-flex>
        <v-flex xs6 sm2 class="px-1 text-xs-right">
          <v-btn color="primary" :loading="generating" :disabled="builder.trackerId == null" @click="generateReport">Generate</v-btn>
        </v-flex>
      </v-layout>
      <v-progress-linear v-if="generating" :value="percentReady" color="primary" height="4" class="mt-2"></v-progress-linear>
      <div v-if="reportId && !generating" class="mt-2">
        <v-btn small flat color="primary" @click="downloadReport"><v-icon left small>file_download</v-icon>Download {{ builder.format }}</v-btn>
      </div>
    </v-card>

    <v-progress-linear
      v-if="loading"
      class="report-view-screen__loading"
      color="primary"
      height="2"
      indeterminate
    />

    <v-card-title v-if="report.title" class="report-view-screen__title">
      {{ report.title }}
    </v-card-title>

    <v-tabs v-if="sheets.length" v-model="activeSheet" grow>
      <v-tab v-for="(sheet, i) in sheets" :key="'tab-' + i">
        {{ sheet.header }}
      </v-tab>

      <v-tab-item v-for="(sheet, i) in sheets" :key="'item-' + i" lazy>
        <component
          :is="section2cmp[section.type] || fallback"
          v-for="(section, si) in sheetSections(sheet)"
          :key="'section-' + i + '-' + si"
          class="report-view-screen__section"
          :section="section"
        />
      </v-tab-item>
    </v-tabs>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import reportViewModule from './report-view.store.js';
import { SECTION_FALLBACK } from './report-view.store.js';
import { request } from '@/api/request';
// Side-effect import: globally registers the section components by their kebab
// names so <component :is="'report-section-*'"> resolves. Idempotent.
import './sections/index.js';

function todayISO() { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }

export default {
  name: 'report-view-screen',

  data() {
    return {
      activeSheet: 0,
      fallback: SECTION_FALLBACK,
      trackerItems: [],
      generating: false,
      builder: { trackerId: null, from: todayISO(), to: todayISO(), format: 'pdf' },
    };
  },

  // Attach the namespaced module before the component's getters are first read.
  beforeCreate() {
    const store = this.$store;
    if (!store) return;
    if (typeof store.registerModuleSafe === 'function') {
      // Engine C's helper: register once (it no-ops if already present, so its state is
      // preserved across route re-entry). NB: do NOT pass preserveState=true here — on a
      // fresh registration that leaves the module's own state uninitialised (Vuex gotcha).
      store.registerModuleSafe('report-view', reportViewModule);
    } else if (!store.hasModule || !store.hasModule('report-view')) {
      store.registerModule('report-view', reportViewModule);
    }
  },

  computed: {
    ...mapGetters('report-view', ['report', 'loading', 'section2cmp', 'percentReady', 'reportId']),
    sheets() {
      return (this.report && this.report.sheets) || [];
    },
  },

  created() {
    // Populate the object picker; preselect ?tracker=<id> from the row-action deep-link.
    request('tracker/list', {}, 'list').then((list) => {
      this.trackerItems = (list || []).map((t) => ({ text: t.label || ('#' + t.id), value: t.id }));
      const want = this.$route.query.tracker;
      if (want != null) this.builder.trackerId = isNaN(Number(want)) ? want : Number(want);
      else if (this.trackerItems.length) this.builder.trackerId = this.trackerItems[0].value;
    }).catch(() => {});
  },

  watch: {
    '$route.params.id': {
      immediate: true,
      handler(id) {
        if (id != null && id !== '') {
          this.$store.dispatch('report-view/fetch', id);
        }
      },
    },
  },

  methods: {
    sheetSections(sheet) {
      return (sheet && sheet.sections) || [];
    },
    // generate → the store polls status @200ms to 100% → we retrieve + render the doc.
    async generateReport() {
      if (this.builder.trackerId == null) return;
      this.generating = true;
      try {
        const reportId = await this.$store.dispatch('report-view/generate', {
          tracker_id: this.builder.trackerId,
          from: this.builder.from,
          to: this.builder.to,
        });
        await this.$store.dispatch('report-view/fetch', reportId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[tracko] report generate failed:', e && e.message);
      } finally {
        this.generating = false;
      }
    },
    downloadReport() {
      this.$store.dispatch('report-view/download', { format: this.builder.format, report_id: this.reportId });
    },
  },
};
</script>

<style scoped>
.report-view-screen {
  position: relative;
  height: 100%;
}
.report-view-screen__loading {
  margin: 0;
}
.report-view-screen__section {
  margin: 12px 0;
}
</style>
